import L from 'leaflet';
import 'leaflet.vectorgrid';
import uuid from 'uuid/v1';
import { set } from 'lodash';

import * as layerOps from '../../api/Layers/layer-operations';
import * as styleOps from '../../api/Styles/style-operations';

import adaptURL from '../adapt-url';
import { getGeoJSONStyle, getPopup, whenClicked } from '../geojson-layer-style';
import loadGeoJSONFromURL from '../load-geojson-from-url';
import loadStyleFromURL from '../load-style-from-url';
import './customPopup';

/**
 * Builds the GeoJSON VT layer.
 *
 * @async
 * @param {object} arg - The object argument.
 * @param {object} arg.layer - The layer object.
 * @param {object} arg.style - The style object.
 * @param {boolean} [arg.isPartOfMap=false] - Flag that is true if layer is part of a map.
 * @param {number} [arg.zIndex=400] - The zIndex for the layer.
 * @returns {Promise<object>} The leaflet layer to add to the map with the geojson and the style.
 */
const buildGeoJSONVTLayer = async ({
  isPartOfMap = false,
  layer,
  popupProp,
  style,
  zIndex = 400
}) => {
  let geojsonFile = '';
  let layerToDraw = '';
  const vectorTileLayerStyles = {};
  let customStyleObject = {};

  try {
    const url = adaptURL(layerOps.getURL(layer));
    geojsonFile = await loadGeoJSONFromURL(url);

    if (styleOps.isCustom(style))
      customStyleObject = await loadStyleFromURL({
        url: adaptURL(styleOps.getStyleURL(style)),
        layer
      });

    const styleOptions = await getGeoJSONStyle({ style, layer, customStyleObject });
    vectorTileLayerStyles.sliced = properties => {
      const propStyle = styleOptions.style(properties);
      propStyle.stroke = true;
      propStyle.fill = true;
      return propStyle;
    };

    const options = {
      rendererFactory: L.canvas.tile,
      attribution: layerOps.getAttribution(layer),
      subdomains: layerOps.getSubdomains(layer), // 01234 for openmaptiles, abcd for mapbox
      maxNativeZoom: layerOps.getMaxZoom(layer),
      maxZoom: layerOps.getMaxZoom(layer), // max zoom to preserve detail on; can't be higher than 24
      tolerance: styleOps.getTolerance(style), // simplification tolerance (higher means simpler)
      extent: 4096, // tile extent (both width and height)
      buffer: 64, // tile buffer on each side
      debug: 0, // logging level (0 to disable, 1 or 2)
      lineMetrics: false, // whether to enable line metrics tracking for LineString/MultiLineString features
      promoteId: null, // name of a feature property to promote to feature.id. Cannot be used with `generateId`
      generateId: false, // whether to generate feature ids. Cannot be used with `promoteId`
      indexMaxZoom: 5, // max zoom in the initial tile index
      indexMaxPoints: 100000, // max number of points per tile in the index
      interactive: true,
      vectorTileLayerStyles,
      getFeatureId: feature => {
        if (!feature.properties.uuid) set(feature, ['properties', 'uuid'], uuid());
        return feature.properties.uuid;
      }
    };

    if (isPartOfMap) options.pane = `${zIndex}`;

    layerToDraw = L.vectorGrid.slicer(geojsonFile, options);

    let highlight;
    const clearHighlight = () => {
      if (highlight) layerToDraw.resetFeatureStyle(highlight);
      highlight = null;
    };

    layerToDraw.on('click', e => {
      const { latlng } = e;
      const { properties } = e.layer;
      const { _map: leafletMap } = e.target;
      whenClicked({ name: layerOps.getName(layer), properties });
      if (popupProp !== true) {
        L.customPopup()
          .setContent(
            getPopup({
              properties,
              styledProp: styleOps.getStyledProp(style),
              name: layerOps.getName(layer)
            })
          )
          .setLatLng(latlng)
          .openOn(leafletMap);
        clearHighlight();
      }
      highlight = e.layer.properties.uuid;
      const propStyle = styleOptions.style(properties);
      layerToDraw.setFeatureStyle(highlight, {
        ...propStyle,
        color: '#666',
        dashArray: '',
        weight: 9,
        stroke: true,
        fill: true
      });
      L.DomEvent.stop(e);
    });

    return layerToDraw;
  } catch (error) {
    console.error(error);
    throw new Error(error.message);
  }
};

export default buildGeoJSONVTLayer;
